import { useEffect, useState } from 'react';
import { Page } from '@/components/Page';
import { Section } from '@/components/Section';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { sb } from '@/hooks/useSupabaseQuery';
import { useAuth } from '@/providers/AuthProvider';
import { useHaptic } from '@/hooks/useHaptic';
import type { UserSettings } from '@/types/domain';

type FeatureName = 'alpha' | 'beta' | 'gamma' | 'delta' | 'epsilon' | 'zeta' | 'eta' | 'theta';

const FEATURES: { name: FeatureName; label: string }[] = [
  { name: 'alpha', label: 'Alpha' },
  { name: 'beta', label: 'Beta' },
  { name: 'gamma', label: 'Gamma' },
  { name: 'delta', label: 'Delta' },
  { name: 'epsilon', label: 'Epsilon' },
  { name: 'zeta', label: 'Zeta' },
  { name: 'eta', label: 'Eta' },
  { name: 'theta', label: 'Theta' },
];

const FEAT_INIT: Record<FeatureName, boolean> = {
  alpha: false,
  beta: false,
  gamma: false,
  delta: false,
  epsilon: false,
  zeta: false,
  eta: false,
  theta: false,
};

/** Биржи, для которых можно настроить API-ключи. */
const EXCHANGE_KEY_CONFIGS: { id: string; label: string; hasPassphrase: boolean }[] = [
  { id: 'gate', label: 'Gate.io', hasPassphrase: false },
  { id: 'deribit', label: 'Deribit', hasPassphrase: false },
  { id: 'okx', label: 'OKX', hasPassphrase: true },
  { id: 'binance', label: 'Binance', hasPassphrase: false },
  { id: 'bybit', label: 'Bybit', hasPassphrase: false },
  { id: 'htx', label: 'HTX', hasPassphrase: false },
  { id: 'mexc', label: 'MEXC', hasPassphrase: false },
  { id: 'delta', label: 'Delta Exchange', hasPassphrase: false },
  { id: 'coincall', label: 'Coincall', hasPassphrase: true },
  { id: 'bingx', label: 'BingX', hasPassphrase: false },
  { id: 'kucoin', label: 'KuCoin', hasPassphrase: true },
  { id: 'blofin', label: 'BloFin', hasPassphrase: false },
  { id: 'weex', label: 'WEEX', hasPassphrase: false },
  { id: 'xt', label: 'XT.com', hasPassphrase: false },
  { id: 'kcex', label: 'KCEX', hasPassphrase: true },
  { id: 'bitget', label: 'Bitget', hasPassphrase: false },
];

interface ExKey { apiKey: string; apiSecret: string; passphrase: string; }
type ExchKeysState = Record<string, ExKey>;

/** Тип строки из exchange_api_keys. */
interface ExchangeApiKeyRow {
  exchange: string;
  api_key: string | null;
  api_secret: string | null;
  passphrase: string | null;
}

type BotExchangeName = 'fxpro' | 'vantage' | 'weex';

interface BotExchangeRow {
  exchange: BotExchangeName;
  api_key: string | null;
  api_secret: string | null;
  passphrase: string | null;
  mt_login: string | null;
  mt_password: string | null;
  mt_server: string | null;
  is_demo: boolean;
  is_enabled: boolean;
  status: string | null;
  ws_status: string | null;
  last_sync_at: string | null;
  last_error: string | null;
  balance_json: { currency: string; available: number; equity: number } | null;
}

const BOT_EXCHANGES: { id: BotExchangeName; label: string; supportsDemo: boolean }[] = [
  { id: 'fxpro', label: 'FxPro', supportsDemo: true },
  { id: 'vantage', label: 'Vantage', supportsDemo: true },
  { id: 'weex', label: 'Weex', supportsDemo: false },
];

const emptyKey = (): ExKey => ({ apiKey: '', apiSecret: '', passphrase: '' });

const initExchKeys = (): ExchKeysState =>
  Object.fromEntries(EXCHANGE_KEY_CONFIGS.map((e) => [e.id, emptyKey()]));

function compactErrorMessage(message: string | null | undefined): string | null {
  if (!message) return null;
  if (message.includes('<!DOCTYPE html') || message.includes('<html')) {
    return 'Ошибка API. Детали в console.error';
  }
  const firstLine = message.split('\n')[0]?.trim() ?? '';
  if (!firstLine) return null;
  return firstLine.length > 180 ? `${firstLine.slice(0, 180)}…` : firstLine;
}


/** Раздел настроек: ключи Gate API и переключатели уведомлений. */
export function SettingsPage(): JSX.Element {
  const { userId } = useAuth();
  const { notify } = useHaptic();
  const [tab, setTab] = useState<'api' | 'features'>('api');
  const [key, setKey] = useState('');
  const [secret, setSecret] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [featStates, setFeatStates] = useState<Record<FeatureName, boolean>>(FEAT_INIT);
  const [featLoading, setFeatLoading] = useState<Record<FeatureName, boolean>>(FEAT_INIT);
  const [notifyStates, setNotifyStates] = useState<Record<FeatureName, boolean>>(
    () => Object.fromEntries(FEATURES.map(({ name }) => [name, true])) as Record<FeatureName, boolean>,
  );
  const [notifyLoading, setNotifyLoading] = useState<Record<FeatureName, boolean>>(FEAT_INIT);
  const [featError, setFeatError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [gateSaveError, setGateSaveError] = useState<string | null>(null);
  const [exchSaveError, setExchSaveError] = useState<Record<string, string | null>>({});
  const [gateApiError, setGateApiError] = useState<string | null>(null);
  const [gateApiLastOkAt, setGateApiLastOkAt] = useState<string | null>(null);
  const [exchKeys, setExchKeys] = useState<ExchKeysState>(initExchKeys);
  const [exchSaving, setExchSaving] = useState<Record<string, boolean>>({});
  const [botExchanges, setBotExchanges] = useState<Record<BotExchangeName, BotExchangeRow>>({
    fxpro: {
      exchange: 'fxpro',
      api_key: '',
      api_secret: '',
      passphrase: '',
      mt_login: '',
      mt_password: '',
      mt_server: '',
      is_demo: true,
      is_enabled: false,
      status: 'offline',
      ws_status: 'fallback-polling',
      last_sync_at: null,
      last_error: null,
      balance_json: null,
    },
    vantage: {
      exchange: 'vantage',
      api_key: '',
      api_secret: '',
      passphrase: '',
      mt_login: '',
      mt_password: '',
      mt_server: '',
      is_demo: true,
      is_enabled: false,
      status: 'offline',
      ws_status: 'fallback-polling',
      last_sync_at: null,
      last_error: null,
      balance_json: null,
    },
    weex: {
      exchange: 'weex',
      api_key: '',
      api_secret: '',
      passphrase: '',
      mt_login: null,
      mt_password: null,
      mt_server: null,
      is_demo: false,
      is_enabled: false,
      status: 'offline',
      ws_status: 'fallback-polling',
      last_sync_at: null,
      last_error: null,
      balance_json: null,
    },
  });
  const [botSaving, setBotSaving] = useState<Record<BotExchangeName, boolean>>({ fxpro: false, vantage: false, weex: false });
  const [botTesting, setBotTesting] = useState<Record<BotExchangeName, boolean>>({ fxpro: false, vantage: false, weex: false });

  useEffect(() => {
    if (!userId) return;
    // Загружаем user_settings (Gate API + feature flags)
    void sb
      .from('user_settings')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) {
          console.error('[SettingsPage] load error:', error.message);
          setLoadError(error.message);
          return;
        }
        setLoadError(null);
        const s = data as UserSettings | null;
        if (s) {
          setKey(s.gate_api_key ?? '');
          setSecret(s.gate_api_secret ?? '');
          setEnabled(s.notifications_enabled);
          setNotifyStates(
            Object.fromEntries(FEATURES.map(({ name }) => [name, s[`notify_${name}`] ?? true])) as Record<FeatureName, boolean>,
          );
          setGateApiError(s.gate_api_last_error ?? null);
          setGateApiLastOkAt(s.gate_api_last_ok_at ?? null);
          setFeatStates({
            alpha: s.feature_alpha ?? false,
            beta: s.feature_beta ?? false,
            gamma: s.feature_gamma ?? false,
            delta: s.feature_delta ?? false,
            epsilon: s.feature_epsilon ?? false,
            zeta: s.feature_zeta ?? false,
            eta: s.feature_eta ?? false,
            theta: s.feature_theta ?? false,
          });
        }
      });

    // Загружаем exchange_api_keys
    void sb
      .from('exchange_api_keys')
      .select('exchange, api_key, api_secret, passphrase')
      .then(({ data, error }) => {
        if (error) {
          console.error('[SettingsPage] load exchange_api_keys error:', error.message);
          return;
        }
        if (data) {
          setExchKeys((prev) => {
            const next = { ...prev };
            for (const row of data as ExchangeApiKeyRow[]) {
              if (row.exchange in next) {
                next[row.exchange] = {
                  apiKey: row.api_key ?? '',
                  apiSecret: row.api_secret ?? '',
                  passphrase: row.passphrase ?? '',
                };
              }
            }
            return next;
          });
        }
      });

    void sb
      .from('bot_exchanges')
      .select('exchange, api_key, api_secret, passphrase, mt_login, mt_password, mt_server, is_demo, is_enabled, status, ws_status, last_sync_at, last_error, balance_json')
      .eq('user_id', userId)
      .then(({ data, error }) => {
        if (error) {
          console.error('[SettingsPage] load bot_exchanges error:', error.message);
          return;
        }
        if (data) {
          setBotExchanges((prev) => {
            const next = { ...prev };
            for (const row of data as BotExchangeRow[]) {
              if (row.exchange in next) next[row.exchange] = { ...next[row.exchange], ...row };
            }
            return next;
          });
        }
      });
  }, [userId]);

  const toggleFeature = async (feature: FeatureName): Promise<void> => {
    if (!userId) {
      setFeatError('Не авторизован. Откройте приложение через Telegram и попробуйте снова.');
      notify('error');
      return;
    }
    if (featLoading[feature]) return;
    const prevValue = featStates[feature];
    const newValue = !prevValue;
    // Оптимистичное обновление: переключаем сразу, при ошибке откатываем.
    setFeatStates((prev) => ({ ...prev, [feature]: newValue }));
    setFeatLoading((prev) => ({ ...prev, [feature]: true }));
    setFeatError(null);
    const { error } = await sb.from('user_settings').upsert(
      {
        user_id: userId,
        [`feature_${feature}`]: newValue,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' },
    );
    setFeatLoading((prev) => ({ ...prev, [feature]: false }));
    if (error) {
      console.error(`[SettingsPage] toggle ${feature} error:`, error.message);
      setFeatStates((prev) => ({ ...prev, [feature]: prevValue }));
      setFeatError(`Не удалось ${newValue ? 'включить' : 'выключить'} ${feature}: ${error.message}`);
      notify('error');
    } else {
      notify('success');
    }
  };

  const toggleFeatureNotifications = async (feature: FeatureName): Promise<void> => {
    if (!userId) {
      setFeatError('Не авторизован. Откройте приложение через Telegram и попробуйте снова.');
      notify('error');
      return;
    }
    if (notifyLoading[feature] || !enabled || !featStates[feature]) return;
    const prevValue = notifyStates[feature];
    const newValue = !prevValue;
    setNotifyStates((prev) => ({ ...prev, [feature]: newValue }));
    setNotifyLoading((prev) => ({ ...prev, [feature]: true }));
    setFeatError(null);
    const { error } = await sb.from('user_settings').upsert(
      {
        user_id: userId,
        [`notify_${feature}`]: newValue,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' },
    );
    setNotifyLoading((prev) => ({ ...prev, [feature]: false }));
    if (error) {
      setNotifyStates((prev) => ({ ...prev, [feature]: prevValue }));
      setFeatError(`Не удалось изменить уведомления ${feature}: ${error.message}`);
      notify('error');
    } else {
      notify('success');
    }
  };

  const toggleNotifications = async (newValue: boolean): Promise<void> => {
    if (!userId) return;
    setEnabled(newValue);
    const { error } = await sb.from('user_settings').upsert(
      {
        user_id: userId,
        notifications_enabled: newValue,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' },
    );
    if (error) {
      console.error('[SettingsPage] toggleNotifications error:', error.message);
      setEnabled(!newValue);
      notify('error');
    } else {
      notify('success');
    }
  };

  const saveApiKeys = async (): Promise<void> => {
    if (!userId) {
      setGateSaveError('Не авторизован — ключи не сохранены.');
      notify('error');
      return;
    }
    setGateSaveError(null);
    const { error } = await sb.from('user_settings').upsert(
      {
        user_id: userId,
        gate_api_key: key || null,
        gate_api_secret: secret || null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' },
    );
    if (error) {
      console.error('[SettingsPage] saveApiKeys error:', error.message);
      setGateSaveError(error.message);
      notify('error');
      return;
    }
    // Держим блок «Gate.io API» (exchange_api_keys) в синхроне с «Gate API».
    if (key.trim() && secret.trim()) {
      const { error: exchError } = await sb.from('exchange_api_keys').upsert(
        {
          user_id: userId,
          exchange: 'gate',
          api_key: key.trim(),
          api_secret: secret.trim(),
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id,exchange' },
      );
      if (exchError) {
        console.error('[SettingsPage] saveApiKeys exchange_api_keys error:', exchError.message);
        setGateSaveError(exchError.message);
        notify('error');
        return;
      }
      setExchKeys((prev) => ({ ...prev, gate: { ...(prev.gate ?? emptyKey()), apiKey: key.trim(), apiSecret: secret.trim() } }));
    }
    notify('success');
  };

  const saveExchangeKey = async (exchangeId: string): Promise<void> => {
    if (!userId) {
      setExchSaveError((prev) => ({ ...prev, [exchangeId]: 'Не авторизован — ключи не сохранены.' }));
      notify('error');
      return;
    }
    const k = exchKeys[exchangeId] ?? emptyKey();
    setExchSaving((prev) => ({ ...prev, [exchangeId]: true }));
    setExchSaveError((prev) => ({ ...prev, [exchangeId]: null }));
    const { error } = await sb.from('exchange_api_keys').upsert(
      {
        user_id: userId,
        exchange: exchangeId,
        api_key: k.apiKey || null,
        api_secret: k.apiSecret || null,
        passphrase: k.passphrase || null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,exchange' },
    );
    setExchSaving((prev) => ({ ...prev, [exchangeId]: false }));
    if (error) {
      console.error(`[SettingsPage] saveExchangeKey ${exchangeId}:`, error.message);
      setExchSaveError((prev) => ({ ...prev, [exchangeId]: error.message }));
      notify('error');
    } else {
      if (exchangeId === 'gate' && k.apiKey.trim() && k.apiSecret.trim()) {
        // Триггер в БД копирует Gate-ключи в user_settings (их читают Alpha и poller-equity).
        setKey(k.apiKey.trim());
        setSecret(k.apiSecret.trim());
        setGateApiError(null);
      }
      notify('success');
    }
  };

  const updateExchKey = (id: string, field: keyof ExKey, value: string): void => {
    setExchKeys((prev) => ({ ...prev, [id]: { ...(prev[id] ?? emptyKey()), [field]: value } }));
  };

  const updateBotExchange = (
    id: BotExchangeName,
    field: keyof Pick<BotExchangeRow, 'api_key' | 'api_secret' | 'passphrase' | 'mt_login' | 'mt_password' | 'mt_server' | 'is_demo' | 'is_enabled'>,
    value: string | boolean,
  ): void => {
    setBotExchanges((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const saveBotExchange = async (id: BotExchangeName): Promise<void> => {
    if (!userId) return;
    const row = botExchanges[id];
    setBotSaving((prev) => ({ ...prev, [id]: true }));
    const payload = {
      user_id: userId,
      exchange: id,
      is_demo: row.is_demo,
      is_enabled: row.is_enabled,
      updated_at: new Date().toISOString(),
      ...(id === 'weex'
        ? {
            api_key: row.api_key || null,
            api_secret: row.api_secret || null,
            passphrase: row.passphrase || null,
            mt_login: null,
            mt_password: null,
            mt_server: null,
          }
        : {
            api_key: null,
            api_secret: null,
            passphrase: null,
            mt_login: row.mt_login || null,
            mt_password: row.mt_password || null,
            mt_server: row.mt_server || null,
          }),
    };
    const { error } = await sb.from('bot_exchanges').upsert(
      payload,
      { onConflict: 'user_id,exchange' },
    );
    setBotSaving((prev) => ({ ...prev, [id]: false }));
    if (error) {
      console.error(`[SettingsPage] save bot_exchanges ${id}:`, error.message);
      notify('error');
      return;
    }
    notify('success');
  };

  const testBotExchange = async (id: BotExchangeName): Promise<void> => {
    if (!userId) return;
    setBotTesting((prev) => ({ ...prev, [id]: true }));
    await sb
      .from('bot_exchanges')
      .update({ updated_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('exchange', id);

    const { data } = await sb
      .from('bot_exchanges')
      .select('exchange, api_key, api_secret, passphrase, mt_login, mt_password, mt_server, is_demo, is_enabled, status, ws_status, last_sync_at, last_error, balance_json')
      .eq('user_id', userId)
      .eq('exchange', id)
      .maybeSingle();
    if (data) {
      if (data.last_error) {
        console.error(`[Settings][${id}] full error:`, data.last_error);
      }
      setBotExchanges((prev) => ({ ...prev, [id]: { ...prev[id], ...(data as BotExchangeRow) } }));
    }
    setBotTesting((prev) => ({ ...prev, [id]: false }));
    notify('success');
  };

  return (
    <Page title="Настройки">
      <div role="tablist" aria-label="Разделы настроек" style={{ display: 'flex', margin: '0 var(--space-md) var(--space-md)', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--tg-separator)', overflow: 'hidden' }}>
        {([{ id: 'api', label: 'API' }, { id: 'features', label: 'Функции и уведомления' }] as const).map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            style={{ flex: 1, minHeight: 36, border: 'none', background: tab === id ? 'var(--tg-button)' : 'transparent', color: tab === id ? 'var(--tg-button-text)' : 'var(--tg-text)', fontSize: 12, cursor: 'pointer', fontWeight: tab === id ? 600 : 400, padding: '0 8px' }}
          >
            {label}
          </button>
        ))}
      </div>
      <div hidden={tab !== 'api'}>
      <Section header="Gate API" footer="Используется только для чтения баланса и опционов. Ключи защищены политиками Row Level Security (RLS) — доступны только вам.">
        <Input label="API Key" value={key} onChange={(e) => setKey(e.target.value)} onBlur={() => void saveApiKeys()} />
        <Input label="API Secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} onBlur={() => void saveApiKeys()} />
        {loadError && (
          <div style={{ padding: 'var(--space-sm) var(--space-lg)', color: 'var(--tg-destructive)', fontSize: 13 }}>
            ⚠️ Не удалось загрузить настройки: {loadError}
          </div>
        )}
        {gateSaveError && (
          <div style={{ padding: 'var(--space-sm) var(--space-lg)', color: 'var(--tg-destructive)', fontSize: 13 }}>
            ⚠️ Не удалось сохранить ключи: {gateSaveError}
          </div>
        )}
        {gateApiError && (
          <div style={{ padding: 'var(--space-sm) var(--space-lg)', color: 'var(--tg-destructive)', fontSize: 13 }}>
            ⚠️ Ошибка Gate API: {gateApiError}
          </div>
        )}
        {!gateApiError && gateApiLastOkAt && (
          <div style={{ padding: 'var(--space-sm) var(--space-lg)', color: '#34c759', fontSize: 13 }}>
            ✓ Последний успешный опрос: {new Date(gateApiLastOkAt).toLocaleString('ru')}
          </div>
        )}
      </Section>

      {BOT_EXCHANGES.map(({ id, label, supportsDemo }) => {
        const row = botExchanges[id];
        const bal = row.balance_json;
        const shortError = compactErrorMessage(row.last_error);
        const isMt5Broker = id === 'fxpro' || id === 'vantage';
        return (
          <Section
            key={id}
            header={`${label} — API ключи бирж`}
            footer={shortError ? `Ошибка: ${shortError}` : (isMt5Broker
              ? 'FxPro и Vantage подключаются через MetaTrader 5 bridge. Укажите MT5 login / password / server из терминала брокера.'
              : 'Подключение используется в разделе Beta.')}
          >
            {isMt5Broker ? (
              <>
                <Input
                  label="MT Login"
                  value={row.mt_login ?? ''}
                  onChange={(e) => updateBotExchange(id, 'mt_login', e.target.value)}
                />
                <Input
                  label="MT Password"
                  type="password"
                  value={row.mt_password ?? ''}
                  onChange={(e) => updateBotExchange(id, 'mt_password', e.target.value)}
                />
                <Input
                  label="MT Server"
                  value={row.mt_server ?? ''}
                  onChange={(e) => updateBotExchange(id, 'mt_server', e.target.value)}
                />
              </>
            ) : (
              <>
                <Input
                  label="API Key"
                  value={row.api_key ?? ''}
                  onChange={(e) => updateBotExchange(id, 'api_key', e.target.value)}
                />
                <Input
                  label="API Secret"
                  type="password"
                  value={row.api_secret ?? ''}
                  onChange={(e) => updateBotExchange(id, 'api_secret', e.target.value)}
                />
                {id === 'weex' && (
                  <>
                    <Input
                      label="API Passphrase"
                      type="password"
                      placeholder="passphrase из настроек API на Weex"
                      value={row.passphrase ?? ''}
                      onChange={(e) => updateBotExchange(id, 'passphrase', e.target.value)}
                    />
                    <div style={{ padding: '0 var(--space-lg) var(--space-sm)', color: 'var(--tg-hint)', fontSize: 12 }}>
                      Passphrase задаётся при создании API key в личном кабинете Weex. Без него Weex возвращает 401 / code -1049.
                    </div>
                  </>
                )}
              </>
            )}
            {supportsDemo && (
              <label style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-sm) var(--space-lg)' }}>
                <span>Демо-счёт</span>
                <input type="checkbox" checked={row.is_demo} onChange={(e) => updateBotExchange(id, 'is_demo', e.target.checked)} />
              </label>
            )}
            <label style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-sm) var(--space-lg)' }}>
              <span>Включить биржу</span>
              <input type="checkbox" checked={row.is_enabled} onChange={(e) => updateBotExchange(id, 'is_enabled', e.target.checked)} />
            </label>
            <div style={{ padding: '0 var(--space-lg) var(--space-sm)', color: 'var(--tg-hint)', fontSize: 12 }}>
              Статус: {row.status ?? 'offline'} · WebSocket: {row.ws_status ?? 'fallback-polling'}
              {row.last_sync_at ? ` · Синхронизация: ${new Date(row.last_sync_at).toLocaleString('ru')}` : ''}
            </div>
            {bal && (
              <div style={{ padding: '0 var(--space-lg) var(--space-sm)', color: 'var(--tg-hint)', fontSize: 12 }}>
                Баланс: {bal.available} {bal.currency} · Equity: {bal.equity} {bal.currency}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, padding: 'var(--space-sm) var(--space-lg)' }}>
              <Button variant="primary" disabled={botSaving[id]} onClick={() => void saveBotExchange(id)} style={{ width: '100%' }}>
                {botSaving[id] ? '…' : 'Сохранить'}
              </Button>
              <Button variant="secondary" disabled={botTesting[id]} onClick={() => void testBotExchange(id)} style={{ width: '100%' }}>
                {botTesting[id] ? '…' : 'Проверить соединение'}
              </Button>
            </div>
          </Section>
        );
      })}

      {EXCHANGE_KEY_CONFIGS.map(({ id, label, hasPassphrase }) => {
        const k = exchKeys[id] ?? emptyKey();
        const isSaving = exchSaving[id] ?? false;
        const saveError = exchSaveError[id] ?? null;
        return (
          <Section
            key={id}
            header={`${label} API`}
            footer="Ключи хранятся в Supabase и защищены RLS — доступны только вам."
          >
            <Input
              label="API Key"
              value={k.apiKey}
              onChange={(e) => updateExchKey(id, 'apiKey', e.target.value)}
            />
            <Input
              label="API Secret"
              type="password"
              value={k.apiSecret}
              onChange={(e) => updateExchKey(id, 'apiSecret', e.target.value)}
            />
            {hasPassphrase && (
              <Input
                label="Passphrase"
                type="password"
                value={k.passphrase}
                onChange={(e) => updateExchKey(id, 'passphrase', e.target.value)}
              />
            )}
            <div style={{ padding: 'var(--space-sm) var(--space-lg)' }}>
              <Button
                variant="primary"
                disabled={isSaving}
                onClick={() => void saveExchangeKey(id)}
              >
                {isSaving ? '…' : 'Сохранить'}
              </Button>
            </div>
            {saveError && (
              <div style={{ padding: '0 var(--space-lg) var(--space-sm)', color: 'var(--tg-destructive)', fontSize: 13 }}>
                ⚠️ Не удалось сохранить: {saveError}
              </div>
            )}
          </Section>
        );
      })}

      </div>
      <div hidden={tab !== 'features'}>
      {loadError && (
        <div style={{ padding: 'var(--space-sm) var(--space-lg)', color: 'var(--tg-destructive)', fontSize: 13 }}>
          ⚠️ Не удалось загрузить настройки: {loadError}
        </div>
      )}
      <Section header="Уведомления">
        <label style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-md) var(--space-lg)' }}>
          <span>Присылать в Telegram</span>
          <input type="checkbox" checked={enabled} onChange={(e) => void toggleNotifications(e.target.checked)} />
        </label>
      </Section>
      <Section header="Функции">
        {featError && (
          <div style={{ padding: 'var(--space-sm) var(--space-lg)', color: 'var(--tg-destructive)', fontSize: 13 }}>
            Ошибка: {featError}
          </div>
        )}
        {FEATURES.map(({ name, label }) => (
          <div
            key={name}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 'var(--space-sm) var(--space-lg)',
            }}
          >
            <span>{label}</span>
            <div style={{ display: 'flex', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                <span>Функция</span>
                <input type="checkbox" aria-label={`${label}: функция`} checked={featStates[name]} disabled={featLoading[name]} onChange={() => void toggleFeature(name)} />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, opacity: enabled && featStates[name] ? 1 : 0.5 }}>
                <span>Уведомления</span>
                <input type="checkbox" aria-label={`${label}: уведомления`} checked={notifyStates[name]} disabled={notifyLoading[name] || !enabled || !featStates[name]} onChange={() => void toggleFeatureNotifications(name)} />
              </label>
            </div>
          </div>
        ))}
      </Section>
      </div>
    </Page>
  );
}
